import { useCallback, useRef, useState } from "react";
import { Form, Spinner, Table } from "react-bootstrap";
import { BsX } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import bookingFilterSlice from "../booking-filters/store/slice";
import styles from "./bookingSearch.module.scss";
import bookingSearchSlice from "./store/slice";

const BookingSearch = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [focused, setFocused] = useState<boolean>(false);

  const data = useSelector((state) => state.bookingSearch.data);
  const meta = useSelector((state) => state.bookingSearch.meta);
  const archivedData = useSelector((state) => state.bookingSearch.archivedData);
  const archivedMeta = useSelector((state) => state.bookingSearch.archivedMeta);
  const isLoading = useSelector((state) => state.bookingSearch.isLoading);
  const search = useSelector((state) => state.bookingSearch.search);
  const archived = useSelector((state) => state.bookingFilters.archived);

  const input = useRef<HTMLInputElement>(null);

  const setSearch = useCallback(
    (query: string) => dispatch(bookingSearchSlice.actions.setSearch(query)),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(bookingSearchSlice.actions.reset()),
    [dispatch]
  );
  const setSearchFilter = useCallback(
    (query: string) =>
      dispatch(bookingFilterSlice.actions.setSearchFilter(query)),
    [dispatch]
  );
  const toggleArchived = useCallback(
    () => dispatch(bookingFilterSlice.actions.toggleArchived()),
    [dispatch]
  );

  const setFilter = (isArchived: boolean = false) => {
    if (search === "" || isLoading) return;

    if (isArchived && !archived) {
      toggleArchived();
    }

    setSearchFilter(search);
    setFocused(false);
    reset();

    if (location.pathname !== "/bookings") {
      navigate("/bookings");
    }

    if (input.current) {
      input.current.blur();
    }
  };

  return (
    <div
      className={styles.container}
      style={{ width: focused ? "100%" : "50%" }}
    >
      <div className={styles.inputDiv}>
        <Form.Control
          ref={input}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for booking"
          maxLength={100}
          className={styles.input}
          onFocus={() => setFocused(true)}
          onBlur={(event) => {
            if (data.length === 0 && archivedData.length === 0 && !isLoading) {
              setFocused(false);
              reset();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !isLoading) {
              setFilter();
            }
          }}
        />
        {search !== "" && (
          <BsX
            onClick={() => {
              reset();

              if (input.current) {
                input.current.focus();
              }
            }}
            className={`${styles.clear} link-secondary`}
          />
        )}
      </div>
      <div
        id="search-results"
        className={`${styles.results} shadow-lg`}
        style={{
          display: focused ? "inline" : "none",
        }}
      >
        {isLoading && (
          <Spinner
            className={`text-primary ${styles.spinner}`}
            animation="border"
          />
        )}
        {!!(
          !isLoading &&
          data.length === 0 &&
          archivedData.length === 0 &&
          search !== ""
        ) && <span className={styles.noResults}>No results found.</span>}
        {!!(
          !isLoading &&
          data.length === 0 &&
          archivedData.length === 0 &&
          search === ""
        ) && (
          <div className={styles.noResults}>
            <p>Try searching for:</p>
            <ul>
              <li>Booking ID</li>
              <li>Customer reference</li>
              <li>Booking reference</li>
              <li>Vehicle number</li>
              <li>Trailer number</li>
            </ul>
            <p>Press "Enter" to set the filter.</p>
          </div>
        )}

        {!isLoading && (
          <>
            {data.length > 0 && (
              <div>
                <div className={styles.header}>
                  <h4 className="text-muted">Ongoing</h4>

                  {meta && (
                    <small className={styles.seeAll}>
                      <Link to="/bookings" onClick={() => setFilter()}>
                        See all ({meta.total})
                      </Link>
                    </small>
                  )}
                </div>
                <Table
                  striped
                  borderless
                  responsive
                  hover
                  className={styles.table}
                >
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>Customer reference</th>
                      <th>Booking reference</th>
                      <th>Vehicle no.</th>
                      <th>Trailer no.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((booking, index) => (
                      <tr key={index}>
                        <td>
                          <Link to={`/bookings/${booking.id}`}>
                            {booking.id}
                          </Link>
                        </td>
                        <td>{booking.customer_reference}</td>
                        <td>{booking.booking_reference}</td>
                        <td>{booking.vehicle_number}</td>
                        <td>{booking.trailer_number}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {archivedData.length > 0 && (
              <div>
                <div className={styles.header}>
                  <h4 className="text-muted">Archived</h4>

                  {archivedMeta && (
                    <small className={styles.seeAll}>
                      <Link to="/bookings" onClick={() => setFilter(true)}>
                        See all ({archivedMeta.total})
                      </Link>
                    </small>
                  )}
                </div>

                <Table
                  striped
                  borderless
                  responsive
                  hover
                  className={styles.table}
                >
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>Customer reference</th>
                      <th>Booking reference</th>
                      <th>Vehicle no.</th>
                      <th>Trailer no.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedData.map((booking, index) => (
                      <tr key={index}>
                        <td>
                          <Link to={`/bookings/${booking.id}`}>
                            {booking.id}
                          </Link>
                        </td>
                        <td>{booking.customer_reference}</td>
                        <td>{booking.booking_reference}</td>
                        <td>{booking.vehicle_number}</td>
                        <td>{booking.trailer_number}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingSearch;
